import gsap from "gsap";
import SETTINGS from "./constants/configAnimation.json";
import { convertPixelsCm, dynamicScale, randomBetweenNumbers } from "./helpers";
import {
  BlobOffsetType,
  BrandColorType,
  BrandColorsEnum,
  ConfigType,
  ParticleFunctionType,
  ShapeTarget,
  TallyLibraryConfigProps,
  TallyLibraryFunction,
  TallyLibraryProps,
  UnitType,
} from "./types";

import { Noise } from "noisejs";

// Create single Noise instance at module level to avoid 90k+ instantiations per second
const globalNoise = new Noise();

export function createTallyLibrary({
  canvas,
  config,
}: TallyLibraryProps): TallyLibraryFunction {
  const context = canvas.getContext("2d");
  const globalWidth = config?.width || canvas.width;
  const globalHeight = config?.height || canvas.height;

  let isGlobalPlaying = true;

  const play = () => {
    isGlobalPlaying = true;
  };

  const pause = () => {
    isGlobalPlaying = false;
  };

  const settings = {
    margin: config?.margin || SETTINGS.margin,
    particleMinRadius: config?.particleMinRadius || SETTINGS.particleMinRadius,
    particleMaxRadius: config?.particleMaxRadius || SETTINGS.particleMaxRadius,
    innerRadius: config?.innerRadius || SETTINGS.innerRadius,
    offsetIndex: config?.offsetIndex || SETTINGS.offsetIndex,
    particleOffset: config?.particleOffset || SETTINGS.particleOffset,
    includeRadius: config?.includeRadius || SETTINGS.includeRadius,
    offsetX: config?.offsetX || SETTINGS.offsetX,
    offsetY: config?.offsetY || SETTINGS.offsetY,
    blobStrength: config?.blobStrength || SETTINGS.blobStrength,
    blobMultiplier: config?.blobMultiplier || SETTINGS.blobMultiplier,
    blobSpeed: config?.blobSpeed || SETTINGS.blobSpeed,
    // `??`, not `||`: an explicit `false` must switch the spin off.
    includeMagnitation:
      config?.includeMagnitation ?? SETTINGS.includeMagnitation,
    magnitationMultiplier:
      config?.magnitationMultiplier || SETTINGS.magnitationMultiplier,
    vangleMultiplier: config?.vangleMultiplier || 1, // Default to 1 (normal speed)
    dotColor:
      (config?.dotColor as BrandColorType) ||
      (SETTINGS.dotColor as BrandColorType),
    particleSpeedFactor:
      config?.particleSpeedFactor || SETTINGS.particleSpeedFactor,
  };

  const animation = {
    particleOffset: 0.9,
    radius: 0,
    noiseMultiplier: 0,
  };

  const tlDashboardAnimation = gsap.timeline({
    defaults: { duration: 3.5, ease: "expo.inOut" },
  });

  const update = (config: TallyLibraryConfigProps) => {
    if (config) {
      for (const key in config) {
        if (config.hasOwnProperty(key)) {
          const value = config[key as keyof TallyLibraryConfigProps];
          if (value !== undefined) {
            settings[key as ConfigType] = value as ConfigType;
          }
        }
      }
    }
  };

  const pulseAnimation = () => {
    const tl = gsap.timeline({
      defaults: {
        duration: 3.5,
        ease: "expo.inOut",
      },
    });

    const particleOffsetFrom = 1000;
    const particleOffsetTo = 300;
    const noiseMultiplier = 3;
    tl.to(
      animation,
      {
        particleOffset: 0,
        noiseMultiplier,
        radius: (globalWidth - SETTINGS.margin * 2) / 2,
      },
      "<",
    );
    tl.fromTo(
      settings,
      { particleOffset: particleOffsetFrom, magnitationMultiplier: 0.1 },
      {
        particleOffset: particleOffsetTo,
        magnitationMultiplier: SETTINGS.magnitationMultiplier,
      },
      "<",
    );
    tl.set(".logo__dot", { opacity: 0 }, "<1");
    tl.to(settings, { particleOffset: 1000, magnitationMultiplier: 0.1 });
    tl.to(
      animation,
      { particleOffset: 0.9, noiseMultiplier: 0, radius: 0 },
      "<",
    );
    tl.set(".interface__dot", { opacity: 0.95 }, ">-=0.8");
    // tl.set(".logo__dot", { opacity: 1 });
  };

  /**
   * Eases the shape toward `target`, for callers that drive it live (the voice
   * orb follows the microphone). Unlike start/stop it leaves the shared
   * dashboard timeline and the page's logo selectors alone.
   */
  const morph = (
    { magnitationMultiplier, ...shape }: ShapeTarget,
    duration = 0.6,
    ease = "power2.out",
  ) => {
    gsap.to(animation, { ...shape, duration, ease, overwrite: "auto" });
    if (magnitationMultiplier !== undefined) {
      gsap.to(settings, {
        magnitationMultiplier,
        duration,
        ease,
        overwrite: "auto",
      });
    }
  };

  const kill = () => {
    gsap.killTweensOf([animation, settings]);
  };

  const startPersistentCircleAnimation = () => {
    gsap.set(animation, {
      particleOffset: 1,
      radius: (globalWidth - SETTINGS.margin * 2) / 2,
      noiseMultiplier: 1, // Enable noise effects for persistent circle animation
    });
  };

  const start = () => {
    const particleOffsetFrom = gsap.utils.random(300, 2000);
    const particleOffsetTo = gsap.utils.random(-300, 300);
    const noiseMultiplier = gsap.utils.random(0, 3);
    tlDashboardAnimation.set(".tally-canvas-conversation", { opacity: 1 });
    tlDashboardAnimation.to(
      animation,
      {
        particleOffset: 0,
        noiseMultiplier,
        radius: (globalWidth - SETTINGS.margin * 2) / 2,
      },
      "<",
    );
    tlDashboardAnimation.fromTo(
      settings,
      { particleOffset: particleOffsetFrom, magnitationMultiplier: 0.1 },
      {
        particleOffset: particleOffsetTo,
        magnitationMultiplier: SETTINGS.magnitationMultiplier,
      },
      "<",
    );
    tlDashboardAnimation.set(".logo__dot", { opacity: 0 }, "<1");
  };

  const stop = () => {
    tlDashboardAnimation.to(settings, {
      particleOffset: settings.particleOffset,
      magnitationMultiplier: settings.magnitationMultiplier,
    });
    tlDashboardAnimation.to(
      animation,
      { particleOffset: 0.9, noiseMultiplier: 0, radius: 0 },
      "<",
    );
    tlDashboardAnimation.set(".logo__dot", { opacity: 1 }, ">-=0.8");
    tlDashboardAnimation.set(".tally-canvas-conversation", { opacity: 0 });
  };

  return {
    createParticle: (unit: UnitType, index: number) => {
      const particle: ParticleFunctionType = {
        x: 0,
        y: 0,

        angle: Math.random() * 2 * Math.PI,
        vangle: (Math.random() * 0.02 - 0.01) * settings.particleSpeedFactor,
        zangle: Math.random() * 1 * Math.PI,

        centerX: (globalWidth - settings.margin) / 2,
        centerY: (globalHeight - settings.margin) / 2,

        maxRadius: dynamicScale(settings.particleMaxRadius, globalWidth),
        minRadius: dynamicScale(settings.particleMinRadius, globalWidth),

        originalZangle: Math.random() * 0.5 * Math.PI,
        r:
          Math.floor(
            Math.random() *
              (dynamicScale(settings.particleMaxRadius, globalWidth) -
                dynamicScale(settings.particleMinRadius, globalWidth) +
                1),
          ) + dynamicScale(settings.particleMinRadius, globalWidth),

        outerRadius: (globalWidth - settings.margin) / 2,
        innerRadius: settings.innerRadius,

        offsetX: settings.offsetIndex
          ? randomBetweenNumbers(
              -settings.particleOffset,
              settings.particleOffset,
            )
          : index % 2 === 0
            ? randomBetweenNumbers(
                -settings.particleOffset,
                settings.particleOffset,
              )
            : 1,
        offsetY: settings.offsetIndex
          ? randomBetweenNumbers(
              -settings.particleOffset,
              settings.particleOffset,
            )
          : index % 2 === 0
            ? randomBetweenNumbers(
                -settings.particleOffset,
                settings.particleOffset,
              )
            : 1,

        Move: function (blobOffset: BlobOffsetType) {
          if (!isGlobalPlaying) {
            return;
          }

          const includeOffsetX =
            convertPixelsCm(this.offsetX, unit) * animation.particleOffset -
            convertPixelsCm(this.offsetX, unit);
          const includeOffsetY =
            convertPixelsCm(this.offsetY, unit) * animation.particleOffset -
            convertPixelsCm(this.offsetY, unit);

          const convertedOffsetX = convertPixelsCm(settings.offsetX, unit);
          const convertedOffsetY = convertPixelsCm(settings.offsetY, unit);
          const convertedMargin = settings.margin / 2;

          this.angle +=
            convertPixelsCm(this.vangle, unit) * settings.vangleMultiplier;

          if (settings.includeRadius) {
            this.x =
              convertedOffsetX +
              convertedMargin +
              this.centerX +
              (this.outerRadius +
                (this.outerRadius - this.innerRadius) * Math.cos(this.zangle)) *
                Math.cos(this.angle * this.originalZangle) +
              includeOffsetX;
            this.y =
              convertedOffsetY +
              convertedMargin / 2 +
              this.centerY +
              (this.outerRadius +
                (this.outerRadius - this.innerRadius) * Math.cos(this.zangle)) *
                Math.sin(this.angle * this.originalZangle) +
              includeOffsetY;
          } else {
            this.x =
              convertedOffsetX +
              convertedMargin +
              this.centerX +
              animation.radius * Math.cos(this.zangle) * Math.sin(this.angle) +
              includeOffsetX;
            this.y =
              convertedOffsetY +
              convertedMargin +
              this.centerY +
              animation.radius * Math.cos(this.angle) +
              includeOffsetY;
          }

          const noiseX = (this.x + blobOffset.x) * settings.blobMultiplier;
          const noiseY = (this.y + blobOffset.y) * settings.blobMultiplier;

          this.x +=
            globalNoise.simplex2(noiseX, noiseY) *
            settings.blobStrength *
            animation.noiseMultiplier;
          this.y +=
            globalNoise.simplex2(noiseY, noiseX) *
            settings.blobStrength *
            animation.noiseMultiplier;

          if (settings.includeMagnitation) {
            this.zangle += settings.magnitationMultiplier;
          } else {
            this.zangle = this.originalZangle;
          }

          if (context) {
            context.beginPath();
            context.fillStyle = BrandColorsEnum[settings.dotColor];
            context.arc(particle.x, particle.y, particle.r, 0, Math.PI * 2);
            context.fill();
          }
        },
      };
      return particle;
    },
    play,
    pause,
    update,
    pulseAnimation,
    morph,
    kill,
    start,
    stop,
    startPersistentCircleAnimation,
  };
}
